import React, { useState, useEffect, useMemo } from "react";
import useUpdateProfile from "../Libs/useUpdateVesselOwnerProfile";
import { useReadContactPerson, useUpdateContactPerson } from "../Libs/useFetcAndUpdateContactPseron";
import { HiOutlineXMark } from "react-icons/hi2";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useNavigate } from "react-router-dom";
import { useContext } from "react";
import { AuthContext } from "../../Context/AuthContext";
import { IoArrowBack } from "react-icons/io5";
import { useQueryClient } from "react-query";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const VesselOwnerProfile = () => {
  const { mutation } = useUpdateProfile();
  const { data: contactPersonData, isLoading: contactLoading } = useReadContactPerson();
  const { mutate: updateContactPerson, isLoading: updatingContact } = useUpdateContactPerson();
  const { profileData, profileLoading } = useContext(AuthContext);
  const [activeSection, setActiveSection] = useState("profile_v");
  const queryClient = useQueryClient();
  const activeStyle = {
    border: "1px solid #0963EB",
  };

  const navigate = useNavigate();
  
  // Extract contact person data from the nested structure
  const contactPerson = useMemo(() => {
    if (!contactPersonData?.data?.contactPerson) return {};
    return {
      contactName: contactPersonData.data.contactPerson.contactName || "",
      emailAddress: contactPersonData.data.contactPerson.emailAddress || "",
      phoneNumber: contactPersonData.data.contactPerson.phoneNumber || "",
      lga: contactPersonData.data.contactPerson.lga || "",
      state: contactPersonData.data.contactPerson.state || "",
      city: contactPersonData.data.contactPerson.city || "",
      country: contactPersonData.data.contactPerson.country || "",
    };
  }, [contactPersonData]);

  const [formData, setFormData] = useState({
    address: profileData?.address || "",
    state: profileData?.state || "",
    companyName: profileData?.companyName || "",
    website: profileData?.website || "",
    phoneNumber: profileData?.phoneNumber || "",
    linkedIn: profileData?.linkedIn || "",
    email: profileData?.email || "",
    country: profileData?.country || "",
    city: profileData?.city || "",
  });

  const [contactFormData, setContactFormData] = useState({
    contactName: "",
    emailAddress: "",
    phoneNumber: "",
    state: "",
    city: "",
    country: "",
  });

  const [editOverlays, setEditOverlays] = useState({
    address: false,
    state: false,
    companyName: false,
    website: false,
    phoneNumber: false,
    registrationNumber: false,
    linkedIn: false,
    email: false,
    country: false,
    city: false,
    contactName: false,
    contactEmail: false,
    contactPhone: false,
    contactState: false,
    contactCity: false,
    contactCountry: false,
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prevData) => ({
      ...prevData,
      [name]: value.trim() !== "" ? value : null,
    }));
  };

  const handleContactInputChange = (e) => {
    const { name, value } = e.target;
    setContactFormData((prevData) => ({
      ...prevData,
      [name]: value.trim() !== "" ? value : null,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const updatedFields = {};

    for (const key in formData) {
      if (formData[key] !== profileData[key]) {
        updatedFields[key] = formData[key];
      }
    }

    try {
      // Use mutateAsync instead of await
      mutation.mutateAsync(updatedFields, {
        onSuccess: () => {
          // Invalidate the profile query to refetch the data
          queryClient.invalidateQueries("profile");
          toast.success("Profile updated successfully!");
          // Only close the overlay after successful update
          setEditOverlays((prev) => {
            const newOverlays = { ...prev };
            // Find which overlay is currently open
            for (const key in newOverlays) {
              if (newOverlays[key]) {
                newOverlays[key] = false;
                break;
              }
            }
            return newOverlays;
          });
        },
        onError: (error) => {
          toast.error(error.message || "Failed to update profile");
        }
      });
    } catch (error) {
      toast.error(error.message || "Failed to update profile");
    }
  };

  // Update contact form data when contact person data changes
  useEffect(() => {
    if (contactPerson) {
      setContactFormData({
        contactName: contactPerson.contactName || "",
        emailAddress: contactPerson.emailAddress || "",
        phoneNumber: contactPerson.phoneNumber || "",
        state: contactPerson.state || "",
        city: contactPerson.city || "",
        country: contactPerson.country || "",
      });
    }
  }, [contactPerson]);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    const updatedFields = {};

    // Only include fields that have changed
    for (const key in contactFormData) {
      if (contactFormData[key] !== contactPerson[key]) {
        updatedFields[key] = contactFormData[key] || null;
      }
    }

    try {
      updateContactPerson(updatedFields, {
        onSuccess: () => {
          queryClient.invalidateQueries("contactPerson");
          toast.success("Contact person updated successfully!");
          setEditOverlays((prev) => {
            const newOverlays = { ...prev };
            for (const key in newOverlays) {
              if (newOverlays[key]) {
                newOverlays[key] = false;
                break;
              }
            }
            return newOverlays;
          });
        },
        onError: (error) => {
          toast.error(error.message || "Failed to update contact person");
        }
      });
    } catch (error) {
      toast.error(error.message || "Failed to update contact person");
    }
  };

  useEffect(() => {
    setFormData({
      address: profileData?.address || "",
      state: profileData?.state || "",
      companyName: profileData?.companyName || "",
      website: profileData?.website || "",
      phoneNumber: profileData?.phoneNumber || "",
      linkedIn: profileData?.linkedIn || "",
      email: profileData?.email || "",
      country: profileData?.country || "",
      city: profileData?.city || "",
    });
  }, [profileData]);

  const toggleEditOverlay = (fieldName) => {
    setEditOverlays((prevEditOverlays) => {
      const newOverlays = {
        ...prevEditOverlays,
        [fieldName]: !prevEditOverlays[fieldName],
      };
      
      // If we're closing the overlay, remove the active class
      if (!newOverlays[fieldName]) {
        const overlay = document.querySelector(`.overlay-container[data-field="${fieldName}"]`);
        if (overlay) {
          overlay.classList.remove('active');
        }
      }
      
      return newOverlays;
    });
  };

  return (
    <div className="update-customer">
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="wrap">
      <div className="switch">
        <div
          className="per-info"
          onClick={() => {
            setActiveSection("profile_v");
            navigate("/dashboard/profile_v");
          }}
          style={activeSection === "profile_v" ? activeStyle : {}}
        >
          <h3>Company information</h3>
          <p>Manage company information</p>
        </div>
        <div className="document" onClick={() => {
          setActiveSection("updates");
          navigate("/dashboard/updates")
        }}
        style={activeSection === "updates" ? activeStyle : {}}
        >
          <h3>Documents</h3>
          <p>Update License and other documents</p>
        </div>

        <div className="change-pass" onClick={() => {
          setActiveSection("updatevpass");
          navigate("/dashboard/updatevpass")
        }}
        style={activeSection === "updatevpass" ? activeStyle : {}}
        >
          <h3>Password</h3>
          <p>Change and manage password</p>
        </div>
      </div>
      <div className="card">
      <IoArrowBack onClick={() => navigate("/mobile_v")} className="mobile-nav"  />
        <h2>Company information</h2>
        <form onSubmit={handleSubmit}>
          <div className="data-card">
            <label>
              <div className="data">
                <h3>Website</h3>
                {profileLoading ? (
                  <Skeleton count={1} height={10} width={50} />
                ) : (
                  <p>
                    {" "}
                    {profileData?.website ? (
                      <a 
                        href={profileData.website.startsWith('http') ? profileData.website : `https://${profileData.website}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="external-link"
                      >
                        {profileData.website}
                      </a>
                    ) : (
                      "Not provided"
                    )}
                  </p>
                )}
              </div>
              <span onClick={() => toggleEditOverlay("website")}>Edit</span>
              {editOverlays.website && (
                <div className="overlay-container" data-field="website">
                  <div className="card">
                    <div className="header">
                      <p>Edit Website</p>
                      <HiOutlineXMark
                        onClick={() => toggleEditOverlay("website")}
                        className="icon"
                      />
                    </div>
                    <div className="in">
                      <p>Website</p>
                      <input
                        type="text"
                        name="website"
                        value={
                          formData.website !== null ? formData.website : ""
                        }
                        onChange={handleInputChange}
                      />
                    </div>
                    <button type="submit" disabled={mutation.isLoading}>
                      {mutation.isLoading ? "Saving..." : "Save"}
                    </button>
                  </div>
                </div>
              )}
            </label>

            <label>
              <div className="data">
                <h3>LinkedIn</h3>
                {profileLoading ? (
                  <Skeleton count={1} height={10} width={50} />
                ) : (
                  <p>
                    {" "}
                    {profileData?.linkedIn ? (
                      <a 
                        href={profileData.linkedIn.startsWith('http') ? profileData.linkedIn : `https://${profileData.linkedIn}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="external-link"
                      >
                        {profileData.linkedIn}
                      </a>
                    ) : (
                      "Not provided"
                    )}
                  </p>
                )}
              </div>
              <span onClick={() => toggleEditOverlay("linkedIn")}>Edit</span>
              {editOverlays.linkedIn && (
                <div className="overlay-container" data-field="linkedIn">
                  <div className="card">
                    <div className="header">
                      <p>Edit linkedIn</p>
                      <HiOutlineXMark
                        onClick={() => toggleEditOverlay("linkedIn")}
                        className="icon"
                      />
                    </div>
                    <div className="in">
                      <p>LinkedIn</p>
                      <input
                        type="text"
                        name="linkedIn"
                        value={
                          formData.linkedIn !== null ? formData.linkedIn : ""
                        }
                        onChange={handleInputChange}
                      />
                    </div>
                    <button type="submit" disabled={mutation.isLoading}>
                      {mutation.isLoading ? "Saving..." : "Save"}
                    </button>
                  </div>
                </div>
              )}
            </label>

            <label>
              <div className="data">
                <h3>Phone Number</h3>
                {profileLoading ? (
                  <Skeleton count={1} height={10} width={50} />
                ) : (
                  <p>
                    {" "}
                    {profileData?.phoneNumber
                      ? profileData?.phoneNumber
                      : "Not provided"}
                  </p>
                )}
              </div>
              <span onClick={() => toggleEditOverlay("phoneNumber")}>Edit</span>
              {editOverlays.phoneNumber && (
                <div className="overlay-container" data-field="phoneNumber">
                  <div className="card">
                    <div className="header">
                      <p>Edit Phone Number</p>
                      <HiOutlineXMark
                        onClick={() => toggleEditOverlay("phoneNumber")}
                        className="icon"
                      />
                    </div>
                    <div className="in">
                      <p>Phone Number</p>
                      <input
                        type="text"
                        name="phoneNumber"
                        value={
                          formData.phoneNumber !== null
                            ? formData.phoneNumber
                            : ""
                        }
                        onChange={handleInputChange}
                      />
                    </div>
                    <button type="submit" disabled={mutation.isLoading}>
                      {mutation.isLoading ? "Saving..." : "Save"}
                    </button>
                  </div>
                </div>
              )}
            </label>

            <label>
              <div className="data">
                <h3>Country</h3>
                {profileLoading ? (
                  <Skeleton count={1} height={10} width={50} />
                ) : (
                  <p>
                    {" "}
                    {profileData?.country ? profileData?.country : "Not provided"}
                  </p>
                )}
              </div>
              <span onClick={() => toggleEditOverlay("country")}>Edit</span>
              {editOverlays.country && (
                <div className="overlay-container" data-field="country">
                  <div className="card">
                    <div className="header">
                      <p>Edit Country</p>
                      <HiOutlineXMark
                        onClick={() => toggleEditOverlay("country")}
                        className="icon"
                      />
                    </div>
                    <div className="in">
                      <p>Country</p>
                      <input
                        type="text"
                        name="country"
                        value={formData.country !== null ? formData.country : ""}
                        onChange={handleInputChange}
                      />
                    </div>
                    <button type="submit" disabled={mutation.isLoading}>
                      {mutation.isLoading ? "Saving..." : "Save"}
                    </button>
                  </div>
                </div>
              )}
            </label>

            <label>
              <div className="data">
                <h3>State</h3>
                {profileLoading ? (
                  <Skeleton count={1} height={10} width={50} />
                ) : (
                  <p>
                    {" "}
                    {profileData?.state ? profileData?.state : "Not provided"}
                  </p>
                )}
              </div>
              <span onClick={() => toggleEditOverlay("state")}>Edit</span>
              {editOverlays.state && (
                <div className="overlay-container" data-field="state">
                  <div className="card">
                    <div className="header">
                      <p>Edit State</p>
                      <HiOutlineXMark
                        onClick={() => toggleEditOverlay("state")}
                        className="icon"
                      />
                    </div>
                    <div className="in">
                      <p>State</p>
                      <input
                        type="text"
                        name="state"
                        value={formData.state !== null ? formData.state : ""}
                        onChange={handleInputChange}
                      />
                    </div>
                    <button type="submit" disabled={mutation.isLoading}>
                      {mutation.isLoading ? "Saving..." : "Save"}
                    </button>
                  </div>
                </div>
              )}
            </label>

            <label>
              <div className="data">
                <h3>City</h3>
                {profileLoading ? (
                  <Skeleton count={1} height={10} width={50} />
                ) : (
                  <p>
                    {" "}
                    {profileData?.city ? profileData?.city : "Not provided"}
                  </p>
                )}
              </div>
              <span onClick={() => toggleEditOverlay("city")}>Edit</span>
              {editOverlays.city && (
                <div className="overlay-container" data-field="city">
                  <div className="card">
                    <div className="header">
                      <p>Edit City</p>
                      <HiOutlineXMark
                        onClick={() => toggleEditOverlay("city")}
                        className="icon"
                      />
                    </div>
                    <div className="in">
                      <p>City</p>
                      <input
                        type="text"
                        name="city"
                        value={formData.city !== null ? formData.city : ""}
                        onChange={handleInputChange}
                      />
                    </div>
                    <button type="submit" disabled={mutation.isLoading}>
                      {mutation.isLoading ? "Saving..." : "Save"}
                    </button>
                  </div>
                </div>
              )}
            </label>

            <label>
              <div className="data">
                <h3>Address</h3>
                {profileLoading ? (
                  <Skeleton count={1} height={10} width={50} />
                ) : (
                  <p>
                    {" "}
                    {profileData?.address
                      ? profileData?.address
                      : "Not provided"}
                  </p>
                )}
              </div>
              <span onClick={() => toggleEditOverlay("address")}>Edit</span>
              {editOverlays.address && (
                <div className="overlay-container" data-field="address">
                  <div className="card">
                    <div className="header">
                      <p>Edit Address</p>
                      <HiOutlineXMark
                        onClick={() => toggleEditOverlay("address")}
                        className="icon"
                      />
                    </div>
                    <div className="in">
                      <p>Address</p>
                      <input
                        type="text"
                        name="address"
                        value={
                          formData.address !== null ? formData.address : ""
                        }
                        onChange={handleInputChange}
                      />
                    </div>
                    <button type="submit" disabled={mutation.isLoading}>
                      {mutation.isLoading ? "Saving..." : "Save"}
                    </button>
                  </div>
                </div>
              )}
            </label>

            <label>
              <div className="data">
                <h3>Company Name</h3>
                {profileLoading ? (
                  <Skeleton count={1} height={10} width={50} />
                ) : (
                  <p>
                    {" "}
                    {profileData?.companyName
                      ? profileData?.companyName
                      : "Not provided"}
                  </p>
                )}
              </div>
              <span onClick={() => toggleEditOverlay("companyName")}>Edit</span>
              {editOverlays.companyName && (
                <div className="overlay-container" data-field="companyName">
                  <div className="card">
                    <div className="header">
                      <p>Edit Company Name</p>
                      <HiOutlineXMark
                        onClick={() => toggleEditOverlay("companyName")}
                        className="icon"
                      />
                    </div>
                    <div className="in">
                      <p>Company Name</p>
                      <input
                        type="text"
                        name="companyName"
                        value={
                          formData.companyName !== null
                            ? formData.companyName
                            : ""
                        }
                        onChange={handleInputChange}
                      />
                    </div>
                    <button type="submit" disabled={mutation.isLoading}>
                      {mutation.isLoading ? "Saving..." : "Save"}
                    </button>
                  </div>
                </div>
              )}
            </label>

            <label>
              <div className="data">
                <h3>Email</h3>
                {profileLoading ? (
                  <Skeleton count={1} height={10} width={50} />
                ) : (
                  <p>
                    {" "}
                    {profileData?.email ? profileData?.email : "Not provided"}
                  </p>
                )}
              </div>
              <span onClick={() => toggleEditOverlay("email")}>Edit</span>
              {editOverlays.email && (
                <div className="overlay-container" data-field="email">
                  <div className="card">
                    <div className="header">
                      <p>Edit Email</p>
                      <HiOutlineXMark
                        onClick={() => toggleEditOverlay("email")}
                        className="icon"
                      />
                    </div>
                    <div className="in">
                      <p>Email</p>
                      <input
                        type="email"
                        name="email"
                        value={formData.email !== null ? formData.email : ""}
                        onChange={handleInputChange}
                      />
                    </div>
                    <button type="submit" disabled={mutation.isLoading}>
                      {mutation.isLoading ? "Saving..." : "Save"}
                    </button>
                  </div>
                </div>
              )}
            </label>
          </div>
        </form>

        {/* Only render contact person section if data is available */}
        {contactPersonData?.data?.contactPerson && (
          <form onSubmit={handleContactSubmit}>
            <h2>Contact Person</h2>
            <div className="data-card">
              <label>
                <div className="data">
                  <h3>Contact Name</h3>
                  {contactLoading ? (
                    <Skeleton count={1} height={10} width={50} />
                  ) : (
                    <p>
                      {contactPerson?.contactName
                        ? contactPerson?.contactName
                        : "Not provided"}
                    </p>
                  )}
                </div>
                <span onClick={() => toggleEditOverlay("contactName")}>Edit</span>
                {editOverlays.contactName && (
                  <div className="overlay-container" data-field="contactName">
                    <div className="card">
                      <div className="header">
                        <p>Edit Contact Name</p>
                        <HiOutlineXMark
                          onClick={() => toggleEditOverlay("contactName")}
                          className="icon"
                        />
                      </div>
                      <div className="in">
                        <p>Contact Name</p>
                        <input
                          type="text"
                          name="contactName"
                          value={contactFormData.contactName || ""}
                          onChange={handleContactInputChange}
                        />
                      </div>
                      <button type="submit" disabled={updatingContact}>
                        {updatingContact ? "Saving..." : "Save"}
                      </button>
                    </div>
                  </div>
                )}
              </label>

              <label>
                <div className="data">
                  <h3>Contact Email</h3>
                  {contactLoading ? (
                    <Skeleton count={1} height={10} width={50} />
                  ) : (
                    <p>
                      {contactPerson?.emailAddress
                        ? contactPerson?.emailAddress
                        : "Not provided"}
                    </p>
                  )}
                </div>
                <span onClick={() => toggleEditOverlay("contactEmail")}>Edit</span>
                {editOverlays.contactEmail && (
                  <div className="overlay-container" data-field="contactEmail">
                    <div className="card">
                      <div className="header">
                        <p>Edit Contact Email</p>
                        <HiOutlineXMark
                          onClick={() => toggleEditOverlay("contactEmail")}
                          className="icon"
                        />
                      </div>
                      <div className="in">
                        <p>Contact Email</p>
                        <input
                          type="email"
                          name="emailAddress"
                          value={contactFormData.emailAddress || ""}
                          onChange={handleContactInputChange}
                        />
                      </div>
                      <button type="submit" disabled={updatingContact}>
                        {updatingContact ? "Saving..." : "Save"}
                      </button>
                    </div>
                  </div>
                )}
              </label>

              <label>
                <div className="data">
                  <h3>Contact Phone Number</h3>
                  {contactLoading ? (
                    <Skeleton count={1} height={10} width={50} />
                  ) : (
                    <p>
                      {contactPerson?.phoneNumber
                        ? contactPerson?.phoneNumber
                        : "Not provided"}
                    </p>
                  )}
                </div>
                <span onClick={() => toggleEditOverlay("contactPhone")}>Edit</span>
                {editOverlays.contactPhone && (
                  <div className="overlay-container" data-field="contactPhone">
                    <div className="card">
                      <div className="header">
                        <p>Edit Contact Phone Number</p>
                        <HiOutlineXMark
                          onClick={() => toggleEditOverlay("contactPhone")}
                          className="icon"
                        />
                      </div>
                      <div className="in">
                        <p>Contact Phone Number</p>
                        <input
                          type="text"
                          name="phoneNumber"
                          value={contactFormData.phoneNumber || ""}
                          onChange={handleContactInputChange}
                        />
                      </div>
                      <button type="submit" disabled={updatingContact}>
                        {updatingContact ? "Saving..." : "Save"}
                      </button>
                    </div>
                  </div>
                )}
              </label>

              <label>
                <div className="data">
                  <h3>Contact Country</h3>
                  {contactLoading ? (
                    <Skeleton count={1} height={10} width={50} />
                  ) : (
                    <p>
                      {contactPerson?.country ? contactPerson?.country : "Not provided"}
                    </p>
                  )}
                </div>
                <span onClick={() => toggleEditOverlay("contactCountry")}>Edit</span>
                {editOverlays.contactCountry && (
                  <div className="overlay-container" data-field="contactCountry">
                    <div className="card">
                      <div className="header">
                        <p>Edit Contact Country</p>
                        <HiOutlineXMark
                          onClick={() => toggleEditOverlay("contactCountry")}
                          className="icon"
                        />
                      </div>
                      <div className="in">
                        <p>Contact Country</p>
                        <input
                          type="text"
                          name="country"
                          value={contactFormData.country || ""}
                          onChange={handleContactInputChange}
                        />
                      </div>
                      <button type="submit" disabled={updatingContact}>
                        {updatingContact ? "Saving..." : "Save"}
                      </button>
                    </div>
                  </div>
                )}
              </label>

              <label>
                <div className="data">
                  <h3>Contact State</h3>
                  {contactLoading ? (
                    <Skeleton count={1} height={10} width={50} />
                  ) : (
                    <p>
                      {contactPerson?.state ? contactPerson?.state : "Not provided"}
                    </p>
                  )}
                </div>
                <span onClick={() => toggleEditOverlay("contactState")}>Edit</span>
                {editOverlays.contactState && (
                  <div className="overlay-container" data-field="contactState">
                    <div className="card">
                      <div className="header">
                        <p>Edit Contact State</p>
                        <HiOutlineXMark
                          onClick={() => toggleEditOverlay("contactState")}
                          className="icon"
                        />
                      </div>
                      <div className="in">
                        <p>Contact State</p>
                        <input
                          type="text"
                          name="state"
                          value={contactFormData.state || ""}
                          onChange={handleContactInputChange}
                        />
                      </div>
                      <button type="submit" disabled={updatingContact}>
                        {updatingContact ? "Saving..." : "Save"}
                      </button>
                    </div>
                  </div>
                )}
              </label>

              <label>
                <div className="data">
                  <h3>Contact City</h3>
                  {contactLoading ? (
                    <Skeleton count={1} height={10} width={50} />
                  ) : (
                    <p>
                      {contactPerson?.city ? contactPerson?.city : "Not provided"}
                    </p>
                  )}
                </div>
                <span onClick={() => toggleEditOverlay("contactCity")}>Edit</span>
                {editOverlays.contactCity && (
                  <div className="overlay-container" data-field="contactCity">
                    <div className="card">
                      <div className="header">
                        <p>Edit Contact City</p>
                        <HiOutlineXMark
                          onClick={() => toggleEditOverlay("contactCity")}
                          className="icon"
                        />
                      </div>
                      <div className="in">
                        <p>Contact City</p>
                        <input
                          type="text"
                          name="city"
                          value={contactFormData.city || ""}
                          onChange={handleContactInputChange}
                        />
                      </div>
                      <button type="submit" disabled={updatingContact}>
                        {updatingContact ? "Saving..." : "Save"}
                      </button>
                    </div>
                  </div>
                )}
              </label>
            </div>
          </form>
        )}
        </div>
      </div>
    </div>
  );
};

export default VesselOwnerProfile;
